import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getEntries, getEntry, deleteEntry } from '../services/api';
import { format } from 'date-fns';
import { el } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { useAuth as useAuthCtx } from '../context/AuthContext';

export default function History() {
  const { selectedFactory, user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [entryDetail, setEntryDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const loadEntries = () => {
    if (!selectedFactory) return;
    setLoading(true);
    getEntries(selectedFactory.id, { from: fromDate || undefined, to: toDate || undefined, limit: 50 })
      .then(res => setEntries(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadEntries(); }, [selectedFactory]);

  const handleSelectEntry = async (entry) => {
    const dateStr = format(new Date(entry.entry_date), 'yyyy-MM-dd');
    setSelectedEntry(dateStr);
    setDetailLoading(true);
    try {
      const res = await getEntry(selectedFactory.id, dateStr);
      setEntryDetail(res.data);
    } catch { setEntryDetail(null); }
    finally { setDetailLoading(false); }
  };

  const handleDelete = async (dateStr) => {
    if (!window.confirm('Διαγραφή αυτής της καταχώρησης;')) return;
    try {
      await deleteEntry(selectedFactory.id, dateStr);
      toast.success('Η καταχώρηση διαγράφηκε');
      setSelectedEntry(null);
      setEntryDetail(null);
      loadEntries();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Σφάλμα');
    }
  };

  const categories = entryDetail
    ? [...new Set(entryDetail.items?.map(i => i.category || 'Άλλα'))]
    : [];

  return (
    <>
      <div className="print-header" style={{ display: 'none' }}>
        <h1>🏭 Factory Tracker — {selectedFactory?.name}</h1>
        <p>Ιστορικό Καταχωρήσεων{entryDetail ? ` — ${format(new Date(entryDetail.entry_date), 'd MMMM yyyy', { locale: el })}` : ''}</p>
      </div>
      <div className="page-header">
        <div className="flex-between">
          <div>
            <h2>Ιστορικό Καταχωρήσεων</h2>
            <p>Προβολή παλαιότερων ημερήσιων καταχωρήσεων</p>
          </div>
          {entryDetail && (
            <button className="btn btn-secondary no-print" onClick={() => window.print()}>🖨 Εκτύπωση / PDF</button>
          )}
        </div>
      </div>
      <div className="page-body">
        <div className="card no-print" style={{ marginBottom: 16 }}>
          <div className="card-body" style={{ padding: '14px 20px' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Από</label>
                <input type="date" className="form-input" value={fromDate} onChange={e => setFromDate(e.target.value)} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Έως</label>
                <input type="date" className="form-input" value={toDate} onChange={e => setToDate(e.target.value)} />
              </div>
              <button className="btn btn-primary" onClick={loadEntries}>Αναζήτηση</button>
              <button className="btn btn-secondary" onClick={() => { setFromDate(''); setToDate(''); setTimeout(loadEntries, 0); }}>Επαναφορά</button>
            </div>
          </div>
        </div>

        <div className="grid-2" style={{ alignItems: 'start' }}>
          <div className="card">
            <div className="card-header">
              <span className="card-title">Καταχωρήσεις</span>
              <span className="badge badge-gray">{entries.length}</span>
            </div>
            <div className="table-wrap">
              {loading ? (
                <div className="page-loading"><span className="loading-spinner dark" /></div>
              ) : entries.length === 0 ? (
                <div className="empty-state"><div>📋</div><p>Δεν βρέθηκαν καταχωρήσεις</p></div>
              ) : (
                <table>
                  <thead>
                    <tr><th>Ημερομηνία</th><th>Σύνολο</th><th>Χρήστης</th></tr>
                  </thead>
                  <tbody>
                    {entries.map(e => {
                      const dateStr = format(new Date(e.entry_date), 'yyyy-MM-dd');
                      return (
                        <tr
                          key={e.id}
                          style={{ cursor: 'pointer', background: selectedEntry === dateStr ? 'var(--blue-light)' : '' }}
                          onClick={() => handleSelectEntry(e)}
                        >
                          <td><strong>{format(new Date(e.entry_date), 'd MMM yyyy', { locale: el })}</strong></td>
                          <td><strong style={{ color: 'var(--blue-dark)' }}>€{parseFloat(e.grand_total).toLocaleString('el-GR', { minimumFractionDigits: 2 })}</strong></td>
                          <td style={{ color: 'var(--gray-400)', fontSize: 12 }}>{e.submitted_by_name}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <span className="card-title">
                {entryDetail ? format(new Date(entryDetail.entry_date), 'd MMMM yyyy', { locale: el }) : 'Λεπτομέρειες'}
              </span>
              {entryDetail && (user?.role === 'super_admin' || user?.role === 'manager') && (
                <button className="btn btn-danger btn-sm" onClick={() => handleDelete(selectedEntry)}>🗑 Διαγραφή</button>
              )}
            </div>
            <div className="card-body">
              {detailLoading ? (
                <div className="page-loading"><span className="loading-spinner dark" /></div>
              ) : !entryDetail ? (
                <div className="empty-state"><div>👆</div><p>Επιλέξτε μια καταχώρηση για λεπτομέρειες</p></div>
              ) : (
                <>
                  {categories.map(cat => {
                    // Group items by product_id so multi-baker entries collapse into one line.
                    const catItems = entryDetail.items?.filter(i => (i.category || 'Άλλα') === cat) || [];
                    const groups = [];
                    const seenByProduct = new Map();
                    catItems.forEach(it => {
                      const key = it.product_id ?? `name:${it.product_name}`;
                      if (seenByProduct.has(key)) {
                        groups[seenByProduct.get(key)].push(it);
                      } else {
                        seenByProduct.set(key, groups.length);
                        groups.push([it]);
                      }
                    });
                    return (
                      <div key={cat}>
                        <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.6px', color: 'var(--gray-400)', fontWeight: 600, padding: '10px 0 6px' }}>{cat}</div>
                        {groups.map((group, gi) => {
                          const first = group[0];
                          const totalQty = group.reduce((s, x) => s + parseFloat(x.quantity || 0), 0);
                          const totalPrice = group.reduce((s, x) => s + parseFloat(x.total_price || 0), 0);
                          const formatQty = (q) => parseFloat(q).toLocaleString('el-GR', { maximumFractionDigits: 3 });
                          return (
                            <div key={first.id || `${cat}-${gi}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--gray-100)' }}>
                              <div>
                                <div style={{ fontWeight: 500 }}>{first.product_name}</div>
                                {group.length === 1 ? (
                                  <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>
                                    €{parseFloat(first.unit_price).toFixed(2)} × {formatQty(first.quantity)}
                                    {first.baker_name && <span> · {first.baker_name}</span>}
                                  </div>
                                ) : (
                                  <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>
                                    €{parseFloat(first.unit_price).toFixed(2)} × {formatQty(totalQty)} συνολικά:
                                    {' '}
                                    {group.map((x, idx) => (
                                      <span key={x.id || idx}>
                                        {idx > 0 && ', '}
                                        {formatQty(x.quantity)} {x.baker_name || '(Κενό)'}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <strong style={{ color: 'var(--blue-dark)' }}>€{totalPrice.toFixed(2)}</strong>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                  {entryDetail.notes && (
                    <div style={{ marginTop: 16, padding: 12, background: 'var(--gray-50)', borderRadius: 8, fontSize: 13, color: 'var(--gray-600)' }}>
                      📝 {entryDetail.notes}
                    </div>
                  )}
                  <div className="total-bar" style={{ marginTop: 16 }}>
                    <span className="total-bar-label">Σύνολο</span>
                    <span className="total-bar-amount">€{parseFloat(entryDetail.grand_total).toLocaleString('el-GR', { minimumFractionDigits: 2 })}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
